import { isEqual } from 'lodash';
import { Component, ReactNode } from 'react';
import { debounce } from '@optimus-web/common';
import { Calendar as CalendarComponent, Modifiers } from '../Calendar';
import { TooltipProps } from '../Tooltip';
import { isNil, noop } from '../utils/func';
import { CalendarService, StructuredDate, StructuredMonth, StructuredYear } from '../utils/services';

export enum RangeSelection {
    End = 'end',
    Start = 'start',
}

interface DateMouseOverArgs<D> {
    startDate: D;
    endDate: D;
}

export interface RangeDatePickerProps<D = StructuredDate, M = StructuredMonth, Y = StructuredYear> {
    getCalendarService: () => CalendarService<D, M, Y>;
    onDateClick: (date: D, selecting: RangeSelection) => void;
    onDateMouseOver: ({ startDate, endDate }: DateMouseOverArgs<D>) => void;
    onRangeChange: (startDate: D, endDate: D) => void;
    allowDisabledClick?: boolean;
    allowOneDateRange?: boolean;
    enableAdjacentMonthSelection?: boolean;
    areSurroundingDaysOfSingleMonthHidden?: boolean;
    endDate?: D;
    isSingleDaySelectionCircle?: boolean;
    modifiers?: Modifiers<D>;
    month?: M;
    numberOfMonths?: number;
    shouldResetOnEarlierStartDate?: boolean;
    getMinLengthHighlight?: (startDate: D) => number;
    startDate?: D;
    startDateWithTooltip?: TooltipProps;
    maxDate?: D;
    minDate?: D;
    leftFooter?: ReactNode;
    rightFooter?: ReactNode;
    getDateTooltip?: (day: D) => ReactNode | undefined;
    tooltipDelay?: number;
}

interface RangeDatePickerState<D> {
    startDate?: D;
    endDate?: D | null;
    selecting: RangeSelection;
    hoveredEndDate: D | null;
}

export class RangeDatePicker<D, M, Y> extends Component<RangeDatePickerProps<D, M, Y>, RangeDatePickerState<D>> {
    private modifiers: Modifiers<D>;

    static defaultProps = {
        onDateClick: noop,
        onDateMouseOver: noop,
        onRangeChange: noop,
        shouldResetOnEarlierStartDate: false,
    };

    constructor(props: RangeDatePickerProps<D, M, Y>) {
        super(props);

        const { startDate, endDate } = props;

        this.state = {
            startDate,
            endDate,
            selecting: RangeSelection.Start,
            hoveredEndDate: null,
        };

        this.modifiers = {
            startDate: this.isStartDate,
            endDate: this.isEndDate,
            inBetweenDate: this.isInBetweenDate,
            isEndOfSelection: this.isEndOfSelection,
            isStartOfSelection: this.isStartOfSelection,
            disabled: this.isDisabled,
        };
    }

    UNSAFE_componentWillReceiveProps({ startDate, endDate }: RangeDatePickerProps<D, M, Y>) {
        if (!isEqual(startDate, this.props.startDate) || !isEqual(endDate, this.props.endDate)) {
            this.setState({ startDate, endDate, selecting: RangeSelection.Start });
        }
    }

    handleDateClick = (event: React.MouseEvent, date: D) => {
        const { selecting } = this.state;
        const { getCalendarService, getMinLengthHighlight } = this.props;

        const isDisabled = this.props.modifiers?.disabled?.(date) ?? false;
        if (this.props.allowDisabledClick === true && isDisabled === true) {
            // user clicked to date which is not selectable

            this.props.onDateClick(date, selecting);
            return;
        }
        const isSelectingEndDate =
            this.props.shouldResetOnEarlierStartDate === true &&
            Boolean(!isNil(this.state.startDate) && getCalendarService().isDateLess(date, this.state.startDate));

        if (selecting === RangeSelection.Start || isSelectingEndDate) {
            this.setState({
                startDate: date,
                endDate: null,
                selecting: RangeSelection.End,
            });

            if (getMinLengthHighlight != null) {
                const hoveredEndDate = getCalendarService().addDays(date, getMinLengthHighlight(date));
                this.setState({ hoveredEndDate });
            }
        } else if (!isNil(this.state.startDate)) {
            const [startDate, endDate] = getCalendarService().getOrderedRange(date, this.state.startDate);
            if (!this.props.allowOneDateRange && getCalendarService().isDateEqual(startDate, endDate)) {
                return;
            }

            this.setState({
                startDate,
                endDate,
                selecting: RangeSelection.Start,
                hoveredEndDate: null,
            });

            this.props.onRangeChange(startDate, endDate);
        }

        this.props.onDateClick(date, selecting);
    };

    handleDateMouseOver = (date: D) => {
        const { getCalendarService, onDateMouseOver } = this.props;
        let hoveredEndDate = null;
        if (this.state.selecting === RangeSelection.End && !isNil(this.state.startDate)) {
            hoveredEndDate = date;
            if (this.props.getMinLengthHighlight) {
                hoveredEndDate = getCalendarService().getOrderedRange(
                    date,
                    getCalendarService().addDays(
                        this.state.startDate,
                        this.props.getMinLengthHighlight(this.state.startDate)
                    )
                )[1];
            }
            const [startDate, endDate] = getCalendarService().getOrderedRange(date, this.state.startDate);
            this.setState({ hoveredEndDate });
            onDateMouseOver({ startDate, endDate });
        } else if (!isNil(hoveredEndDate)) {
            // only set state if hoveredEndDate is other than null
            this.setState({ hoveredEndDate });
        }
    };

    isDisabled = (date: D) => {
        const { getCalendarService, minDate, maxDate } = this.props;
        return getCalendarService().isDateDisabled(date, { minDate, maxDate });
    };

    isStartDate = (date: D) => {
        const { startDate } = this.state;
        return !isNil(startDate) && this.props.getCalendarService().isDateEqual(date, startDate);
    };

    isEndDate = (date: D) => {
        const { endDate } = this.state;
        return !isNil(endDate) && this.props.getCalendarService().isDateEqual(date, endDate);
    };

    isInBetweenDate = (date: D) => {
        const { startDate, endDate, hoveredEndDate } = this.state;
        const { getCalendarService } = this.props;

        const isInBetweeenSelectedDate =
            !isNil(startDate) && !isNil(endDate) && getCalendarService().isDateBetween(date, startDate, endDate);

        const isInbetweenHoveredDate =
            !isNil(startDate) &&
            !isNil(hoveredEndDate) &&
            (getCalendarService().isDateBetween(date, startDate, hoveredEndDate) ||
                getCalendarService().isDateBetween(date, hoveredEndDate, startDate) ||
                getCalendarService().isDateEqual(date, hoveredEndDate));

        return isInBetweeenSelectedDate || isInbetweenHoveredDate;
    };

    isEndOfSelection = (date: D) => {
        const { hoveredEndDate, startDate, endDate } = this.state;
        const { getCalendarService } = this.props;

        const isHoverBefore =
            !isNil(startDate) && !isNil(hoveredEndDate) && getCalendarService().isDateLess(hoveredEndDate, startDate);
        const isStartDate = !isNil(startDate) && getCalendarService().isDateEqual(date, startDate);

        const isEndDate = !isNil(endDate) && getCalendarService().isDateEqual(date, endDate);
        const isEndOfHover =
            !isNil(startDate) &&
            !isNil(hoveredEndDate) &&
            getCalendarService().isDateEqual(date, hoveredEndDate) &&
            getCalendarService().isDateGreaterOrEqual(hoveredEndDate, startDate);
        const isOnlyStartDateSelected = isStartDate && isNil(endDate) && (isNil(hoveredEndDate) || isHoverBefore);

        return isEndDate || isEndOfHover || isOnlyStartDateSelected;
    };

    isStartOfSelection = (date: D) => {
        const { hoveredEndDate, startDate } = this.state;
        const { getCalendarService } = this.props;

        const isHoverBefore =
            !isNil(startDate) && !isNil(hoveredEndDate) && getCalendarService().isDateLess(hoveredEndDate, startDate);

        const isStartDate = !isNil(startDate) && getCalendarService().isDateEqual(date, startDate) && !isHoverBefore;
        const isStartOfHover =
            !isNil(startDate) &&
            !isNil(hoveredEndDate) &&
            getCalendarService().isDateEqual(date, hoveredEndDate) &&
            getCalendarService().isDateLessOrEqual(hoveredEndDate, startDate);

        return isStartDate || isStartOfHover;
    };

    render() {
        const { modifiers, onRangeChange, startDateWithTooltip, ...other } = this.props;

        const calendarProps = {
            ...other,
            modifiers: {
                ...this.modifiers,
                ...modifiers,
            },
            onDateClick: this.handleDateClick,
            onDateMouseOver: debounce(this.handleDateMouseOver, 200),
            startDate: this.state.startDate,
            endDate: this.state.endDate,
            startDateWithTooltip,
            getDateTooltip: this.props.getDateTooltip,
            numberOfMonths: this.props.numberOfMonths ? this.props.numberOfMonths : 2,
        };

        return <CalendarComponent {...calendarProps} />;
    }
}
